import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNewVersion } from '@/hooks/useNewVersion';
import { ROLE_LABELS } from '@/lib/format';
import type { User } from '@/lib/types';
import { AccountModal } from './AccountModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { MigrationBanner } from './MigrationBanner';
import { Avatar } from './ui';
import { ThemeToggle } from './ThemeToggle';
import { Wordmark } from './Wordmark';
import {
  IconChart, IconDashboard, IconLink, IconList, IconLogout, IconMenu, IconReceipt, IconSidebar, IconTarget, IconTrending, IconTrophy,
  IconUserCircle, IconUsers, IconX,
} from './Icons';

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  /** Item restrito a administradores. */
  adminOnly?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

/** O menu pelo fluxo do trabalho: o que se vende, de quem é, e como a equipe vai. */
const NAV: NavGroup[] = [
  {
    title: 'Início',
    items: [{ to: '/', label: 'Painel', icon: <IconDashboard /> }],
  },
  {
    title: 'Vendas',
    items: [
      { to: '/comprovantes', label: 'Comprovantes', icon: <IconReceipt /> },
      { to: '/links', label: 'Meus links', icon: <IconLink /> },
    ],
  },
  {
    title: 'Clientes',
    items: [
      { to: '/clientes', label: 'Clientes', icon: <IconList /> },
      { to: '/alavancagem', label: 'Alavancagem', icon: <IconTrending /> },
    ],
  },
  {
    title: 'Equipe',
    items: [
      { to: '/ranking', label: 'Ranking', icon: <IconTrophy /> },
      { to: '/metas', label: 'Metas mensais', icon: <IconTarget /> },
      { to: '/membros', label: 'Membros', icon: <IconUsers />, adminOnly: true },
      { to: '/metricas', label: 'Métricas', icon: <IconChart /> },
    ],
  },
];

/** Menu recolhido ou não: preferência desta pessoa, neste navegador. */
const COLLAPSE_KEY = 'painel.sidebar';
/** Daqui para cima o menu fica fixo ao lado; abaixo, vira gaveta. A mesma largura do CSS. */
const DESKTOP_QUERY = '(min-width: 1024px)';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean) {
  try {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? 'collapsed' : 'expanded');
  } catch {
    // Armazenamento bloqueado: a escolha vale até recarregar.
  }
}

export function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const groups = NAV.map((group) => ({ ...group, items: group.items.filter((item) => !item.adminOnly || isAdmin) }))
    .filter((group) => group.items.length > 0);
  const { pathname } = useLocation();
  const newVersion = useNewVersion();
  const [newVersionLater, setNewVersionLater] = useState(false);

  const topbarRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Escolher uma tela fecha a gaveta.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Saiu versão nova com a aba aberta: a próxima troca de tela já abre nela.
  // Trocar de tela não perde nada que estava sendo digitado.
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathname.current) return;
    lastPathname.current = pathname;
    if (newVersion) window.location.reload();
  }, [pathname, newVersion]);

  // Gaveta aberta: o resto da tela fica inerte e parado, Esc fecha, e o foco
  // volta ao botão do menu ao fechar. Alargar a janela até o menu fixo também fecha.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const behind = [topbarRef.current, mainRef.current];
    const menuButton = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    const desktop = window.matchMedia(DESKTOP_QUERY);
    const close = () => setDrawerOpen(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.body.style.overflow = 'hidden';
    behind.forEach((element) => element && (element.inert = true));
    closeButtonRef.current?.focus();
    document.addEventListener('keydown', onKeyDown);
    desktop.addEventListener('change', close);
    return () => {
      document.body.style.overflow = previousOverflow;
      behind.forEach((element) => element && (element.inert = false));
      document.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', close);
      if (!desktop.matches) menuButton?.focus();
    };
  }, [drawerOpen]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    saveCollapsed(next);
  }

  function openAccount() {
    setDrawerOpen(false);
    setAccountOpen(true);
  }

  return (
    <div className="layout" data-sidebar={collapsed ? 'collapsed' : 'expanded'} data-drawer={drawerOpen ? 'open' : 'closed'}>
      {/* Só no celular e no tablet: o menu vira gaveta, aberta por aqui. */}
      <header className="topbar" ref={topbarRef}>
        <button
          ref={menuButtonRef}
          type="button"
          className="icon-btn topbar-menu"
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir o menu"
          aria-expanded={drawerOpen}
          aria-controls="app-nav"
        >
          <IconMenu size={20} />
        </button>
        <Wordmark />
        <span className="spacer" />
        <NotificationBell placement="topbar" />
        <button type="button" className="icon-btn topbar-account" onClick={openAccount} aria-label="Minha conta" title="Minha conta">
          {user ? <Avatar name={user.name} color={user.avatarColor} photo={user.photoUrl} size={28} /> : <IconUserCircle />}
        </button>
      </header>

      <div className="drawer-scrim" aria-hidden onClick={() => setDrawerOpen(false)} />

      <nav id="app-nav" className="sidebar" aria-label="Navegação principal">
        <div className="sidebar-head">
          <div className="sidebar-brand">
            <Wordmark />
          </div>
          <button ref={closeButtonRef} type="button" className="icon-btn sidebar-close" onClick={() => setDrawerOpen(false)} aria-label="Fechar o menu">
            <IconX size={18} />
          </button>
        </div>

        <div className="navlist">
          {groups.map((group) => (
            <div key={group.title} className="navgroup" role="group" aria-labelledby={`navgroup-${group.title}`}>
              <span id={`navgroup-${group.title}`} className="navgroup-title">{group.title}</span>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `navbtn${isActive ? ' active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  {item.icon}
                  <span className="navbtn-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="spacer" />

        <div className="sidebar-foot">
          <NotificationBell placement="sidebar" collapsed={collapsed} />
          {user ? <AccountButton user={user} collapsed={collapsed} onOpen={openAccount} /> : null}
          <div className="sidebar-theme">
            <span className="sidebar-theme-label">Tema</span>
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="navbtn sidebar-collapse"
            onClick={toggleCollapsed}
            aria-pressed={collapsed}
            title={collapsed ? 'Expandir o menu' : undefined}
          >
            <IconSidebar />
            <span className="navbtn-label">Recolher o menu</span>
          </button>
          <button type="button" className="navbtn" onClick={() => void logout()} title={collapsed ? 'Sair' : undefined}>
            <IconLogout />
            <span className="navbtn-label">Sair</span>
          </button>
        </div>
      </nav>

      <main className="content" ref={mainRef}>
        <MigrationBanner />
        <Outlet />
      </main>

      {accountOpen ? <AccountModal onClose={() => setAccountOpen(false)} /> : null}

      {newVersion && !newVersionLater ? (
        <div className="update-bar" role="status">
          <span>Versão nova do painel.</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Atualizar</button>
          <button type="button" className="update-bar-later" onClick={() => setNewVersionLater(true)}>Agora não</button>
        </div>
      ) : null}
    </div>
  );
}

/** A pessoa logada: abre "Minha conta". Recolhido, fica só a foto. */
function AccountButton({ user, collapsed, onOpen }: { user: User; collapsed: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="account-button"
      onClick={onOpen}
      title={collapsed ? `${user.name} — minha conta` : 'Minha conta: foto, nome e senha'}
    >
      <Avatar name={user.name} color={user.avatarColor} photo={user.photoUrl} size={30} />
      <span className="account-text" style={{ minWidth: 0, flex: 1 }}>
        <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{user.name}</span>
        <span className="muted truncate" style={{ display: 'block', fontSize: 11.5, marginTop: 1 }}>
          {ROLE_LABELS[user.role]} · {user.login}
        </span>
      </span>
    </button>
  );
}
